<div class="scheduler" [ngClass]="effectiveViewMode()" [style.--resource-count]="resources().length"
  [style.--slot-count]="slots().length">
  <!-- Horizontal view: Resources as columns, time as rows -->
  @if (effectiveViewMode() === 'horizontal') {
    <div class="scheduler-header">
      <div class="corner"></div>
      <div class="scroll-x" #headerScroller>
        <div class="resource-headers">
          @for (res of resources(); track $index) {
            <div class="resource-header" (mouseenter)="onResourceHover($index)" (mouseleave)="onResourceHover(null)">
              @if (resourceTemplate) {
                <ng-container [ngTemplateOutlet]="resourceTemplate!.template"
                [ngTemplateOutletContext]="{ $implicit: res, resource: res }"></ng-container>
              } @else {
                <span class="resource-title">{{ res.title }}</span>
              }
            </div>
          }
        </div>
      </div>
    </div>
    <div class="scheduler-body" #schedulerBody>
      <div class="time-column">
        @for (sl of slots(); track sl.index) {
          <div class="time-slot-label"
            [class.hours-start-horizontal]="isHourStart(sl.start)"
            (mouseenter)="onSlotHover(sl.index)"
            (mouseleave)="onSlotHover(null)">{{ isHourStart(sl.start) ? sl.label : '' }}
          </div>
        }
      </div>
      <div class="scroll-x" #bodyScroller>
        <div #grid class="grid"
          [style.gridTemplateColumns]="'repeat(' + resources().length + ', minmax(var(--resource-min-width, 160px), 1fr))'"
          [style.gridTemplateRows]="'repeat(' + slots().length + ', var(--slot-height, 32px))'">
          <!-- now line for horizontal view -->
          @if (showNowLineEnabled() && inRange()) {
            <div class="now-line horizontal" [style.top.%]="nowPercent()"></div>
          }
          <!-- grid slots clickable -->
          @for (res of resources(); let col = $index; track col) {
            @for (sl of slots(); track sl.index) {
              <div class="grid-cell hover:bg-gray-100" role="button" tabindex="0"
                [ngClass]="{ 'unavailable': !slotAvailable(res.id, sl.index), 'hovered-resource': hoveredResourceIndex() === col, 'hovered-slot': hoveredSlotIndex() === sl.index }"
                [attr.data-time]="sl.label"
                [style.grid-column]="(col + 1)"
                [style.grid-row]="(sl.index + 1)"
                [class.hours-start-horizontal]="isHourStart(sl.start)"
                (mouseenter)="onSlotHover(sl.index); onResourceHover(col)"
                (mouseleave)="onSlotHover(null); onResourceHover(null)"
                (contextmenu)="onSlotContextMenu(res.id, sl, $event)"
              (click)="onSlotClick(res.id, sl)" (keydown.enter)="onSlotClick(res.id, sl)"></div>
            }
          }
          <!-- non-working time bands (time-proportional, behind events) -->
          @for (res of resources(); let col = $index; track col) {
            @for (band of (offBandsByResource()[res.id] || []); track $index) {
              <div class="off-band"
                [style.grid-column]="(col + 1)"
                [style.grid-row]="(slotIndexFor(band._start) + 1)"
                [style.align-self]="'start'"
                [style.height.px]="eventSizePx(band)"
                [style.transform]="'translateY(' + eventOffsetPx(band) + 'px)'">
                <span class="off-label">{{ offLabel() }}</span>
              </div>
            }
          }
          <!-- breaks overlay (time-proportional, behind events) -->
          @for (res of resources(); let col = $index; track col) {
            @for (br of (breaksByResource()[res.id] || []); track $index) {
              <div class="break"
                [ngClass]="{ 'hovered-resource': hoveredResourceIndex() === col, 'hovered-slot': coversSlot(br._start, br._end, hoveredSlotIndex()) }"
                [style.grid-column]="(col + 1)"
                [style.grid-row]="(slotIndexFor(br._start) + 1)"
                [style.align-self]="'start'"
                [style.height.px]="eventSizePx(br)"
                [style.transform]="'translateY(' + eventOffsetPx(br) + 'px)'"
                [style.background]="br.color || 'rgba(0,0,0,0.06)'"
                [attr.title]="br.title || 'Break'">
                <div class="break-label">{{ br.title || 'Break' }}</div>
                <div class="break-time">{{ br._start | date:'shortTime' }} - {{ br._end | date:'shortTime' }}</div>
              </div>
            }
          }
          <!-- live drag preview (horizontal) -->
          @if (dragging()) {
            @if (dragPreview(); as dp) {
              <div class="event drag-ghost"
                [style.grid-column]="(dp.resourceIndex + 1)"
                [style.grid-row]="(dp.slotIndex + 1) + ' / span ' + dp.spanSlots"
                [style.background]="dragEvent() ? eventColor(dragEvent()!) : 'rgba(0,0,0,0.1)'">
                <div class="event-title">{{ dragEvent()?.title || 'Event' }}</div>
                <div class="event-time">{{ dragEvent()?.start | date:'shortTime' }}
                  - {{ dragEvent()?.end | date:'shortTime' }}
                </div>
              </div>
            }
          }
          <!-- live resize preview (horizontal) -->
          @if (resizing()) {
            @if (resizePreview(); as rp) {
              <div class="event drag-ghost"
                [style.grid-column]="(rp.resourceIndex + 1)"
                [style.grid-row]="(rp.slotIndex + 1) + ' / span ' + rp.spanSlots"
                [style.background]="resizeEvent() ? eventColor(resizeEvent()!) : 'rgba(0,0,0,0.1)'">
                <div class="event-title">{{ resizeEvent()?.title || 'Event' }}</div>
                <div class="event-time">{{ resizeEvent()?.start | date:'shortTime' }}
                  - {{ resizeEvent()?.end | date:'shortTime' }}
                </div>
              </div>
            }
          }

          <!-- events overlay -->
          @for (res of resources(); let col = $index; track col) {
            @for (ev of (eventsByResource()[res.id] || []); track ev?.id ?? $index) {
              <div class="event" [attr.role]="ev.extendedProps?.['isHold'] ? 'presentation' : 'button'" [attr.tabindex]="ev.extendedProps?.['isHold'] ? -1 : 0"
                [attr.aria-label]="ev.extendedProps?.['isHold'] ? ev.extendedProps?.['ariaLabel'] : null"
                [ngClass]="{ compact: eventSizePx(ev) < 34, dragging: dragging() && (dragEvent()?.id === ev.id), 'hovered-resource': hoveredResourceIndex() === col, 'hovered-slot': coversSlot(ev._start, ev._end, hoveredSlotIndex()), hold: ev.extendedProps?.['isHold'] }"
                [style.grid-column]="(col + 1)"
                [style.grid-row]="(slotIndexFor(ev._start) + 1)"
                [style.align-self]="'start'"
                [style.height.px]="eventSizePx(ev)"
                [style.transform]="'translateY(' + eventOffsetPx(ev) + 'px)'"
                [style.background]="ev.extendedProps?.['isHold'] ? null : eventColor(ev, res)"
                [style.width]="(ev._lane === ev._lanes - 1) ? 'calc((100% / ' + ev._lanes + ') - var(--event-gutter)' : 'calc(100% / ' + ev._lanes + ')'"
                [style.margin-left]="'calc((' + ev._lane + ') * (100% / ' + ev._lanes + '))'"
                [style.margin-right]="'var(--event-gutter)'"
                [attr.draggable]="false"
                [style.cursor]="ev.extendedProps?.['isHold'] ? 'default' : (isDragEnabled() ? 'grab' : 'pointer')"
                (dragstart)="$event.preventDefault()"
                (contextmenu)="$event.preventDefault()"
                nz-popover
                [nzPopoverTrigger]="ev.extendedProps?.['isHold'] ? null : 'hover'"
                [nzPopoverTitle]="(ev.start | date: 'dd.MM.yyyy') + ' | ' + (ev.start | date:'shortTime') + ' - ' + (ev.end | date: 'shortTime')"
                [nzPopoverContent]="outerPopoverTplH"
                [nzPopoverMouseEnterDelay]="0.15"
                (click)="$event.stopPropagation(); onEventClickGuard(ev)"
                (keydown.enter)="onEventClickGuard(ev)"
                (pointerdown)="onEventPointerDown(ev, $event)">

                @if (isResizeEnabled() && !ev.extendedProps?.['isHold']) {
                  <!-- resize handles for horizontal (top/bottom) -->
                  <div class="resize-handle handle-start horizontal"
                  (pointerdown)="$event.stopPropagation(); onResizePointerDown(ev, 'start', $event)"></div>
                  <div class="resize-handle handle-end horizontal"
                  (pointerdown)="$event.stopPropagation(); onResizePointerDown(ev, 'end', $event)"></div>
                }


                @if (eventTemplate) {
                  <ng-container [ngTemplateOutlet]="eventTemplate!.template"
                  [ngTemplateOutletContext]="{ $implicit: ev, event: ev, resource: res, viewMode: effectiveViewMode() }"></ng-container>
                } @else {
                  <div class="event-title">{{ ev.title || 'Event' }}  {{ res.title }}</div>
                  <div class="event-time">{{ ev._start | date:'shortTime' }} - {{ ev._end | date:'shortTime' }}</div>
                }
                <ng-template #outerPopoverTplH>
                  <ng-container [ngTemplateOutlet]="eventPopoverTemplate?.template || eventPopoverTplH"
                  [ngTemplateOutletContext]="{ $implicit: ev, event: ev, resource: res, viewMode: effectiveViewMode() }"></ng-container>
                </ng-template>
                <ng-template #eventPopoverTplH>
                  <div class="popover-content">
                    <div class="pop-title">{{ ev.title || 'Event' }}</div>
                    <div class="pop-line">{{ ev._start | date:'shortTime' }} - {{ ev._end | date:'shortTime' }}</div>
                    <div class="pop-line">{{ res.title }}</div>
                  </div>
                </ng-template>
              </div>
            }
          }
        </div>
      </div>
    </div>
  } @else {
    <!-- Vertical view: Resources as rows, time as columns -->
    <div class="scheduler-header vertical">
      <div class="corner"></div>
      <div class="scroll-x" #headerScroller>
        <div class="time-headers">
          @for (sl of slots(); track sl.index) {
            <div class="time-header"
              [class.hours-start-vertical]="isHourStart(sl.start)"
              (mouseenter)="onSlotHover(sl.index)"
              (mouseleave)="onSlotHover(null)">{{ isHourStart(sl.start) ? sl.label : '' }}
            </div>
          }
        </div>
      </div>
    </div>
    <div class="scheduler-body vertical" #schedulerBody>
      <div class="resource-column">
        @for (res of resources(); track $index) {
          <div class="resource-row" (mouseenter)="onResourceHover($index)" (mouseleave)="onResourceHover(null)">
            @if (resourceTemplate) {
              <ng-container [ngTemplateOutlet]="resourceTemplate!.template"
              [ngTemplateOutletContext]="{ $implicit: res, resource: res }"></ng-container>
            } @else {
              <span class="resource-title">{{ res.title }}</span>
            }
          </div>
        }
      </div>
      <div class="scroll-x" #bodyScroller>
        <div #grid class="grid vertical"
          [style.gridTemplateRows]="'repeat(' + resources().length + ', var(--row-height, 48px))'"
          [style.gridTemplateColumns]="'repeat(' + slots().length + ', var(--slot-width, 80px))'">
          <!-- now line for vertical view -->
          @if (showNowLineEnabled() && inRange()) {
            <div class="now-line vertical" [style.left.%]="nowPercent()"></div>
          }
          <!-- grid slots clickable -->
          @for (res of resources(); let row = $index; track row) {
            @for (sl of slots(); track sl.index) {
              <div class="grid-cell hover:bg-gray-100 " role="button" tabindex="0"
                [ngClass]="{ 'unavailable': !slotAvailable(res.id, sl.index), 'hovered-resource': hoveredResourceIndex() === row, 'hovered-slot': hoveredSlotIndex() === sl.index }"
                [class.hours-start-vertical]="isHourStart(sl.start)"
                [attr.data-off]="slotOff(res.id, sl.index) ? offLabel() : null"
                [attr.data-time]="sl.label"
                [style.gridRow]="(row + 1)"
                [style.gridColumn]="(sl.index + 1)"
                (mouseenter)="onSlotHover(sl.index); onResourceHover(row)"
                (mouseleave)="onSlotHover(null); onResourceHover(null)"
                (contextmenu)="onSlotContextMenu(res.id, sl, $event)"
              (click)="onSlotClick(res.id, sl)" (keydown.enter)="onSlotClick(res.id, sl)"></div>
            }
          }
          <!-- breaks overlay: switch row/column logic (behind events) -->
          @for (res of resources(); let row = $index; track row) {
            @for (br of (breaksByResource()[res.id] || []); track $index) {
              <div class="break"
                [ngClass]="{ 'hovered-resource': hoveredResourceIndex() === row, 'hovered-slot': coversSlot(br._start, br._end, hoveredSlotIndex()) }"
                [style.gridRow]="(row + 1)"
                [style.gridColumn]="(slotIndexFor(br._start) + 1) + ' / span ' + eventSpanSlots(br)"
                [style.background]="br.color || 'rgba(0,0,0,0.06)'"
                [attr.title]="br.title || 'Break'">
                <span class="break-label">{{ br.title || 'Break' }}</span>
              </div>
            }
          }
          <!-- live drag preview (vertical) -->
          @if (dragging()) {
            @if (dragPreview(); as dp) {
              <div class="event drag-ghost"
                [style.grid-row]="(dp.resourceIndex + 1)"
                [style.grid-column]="(dp.slotIndex + 1) + ' / span ' + dp.spanSlots"
                [style.background]="dragEvent() ? eventColor(dragEvent()!) : 'rgba(0,0,0,0.1)'">
                <div class="event-title">{{ dragEvent()?.title || 'Event' }}</div>
                <div class="event-time">{{ dragEvent()?.start | date:'shortTime' }}
                  - {{ dragEvent()?.end | date:'shortTime' }}
                </div>
              </div>
            }
          }
          <!-- live resize preview (vertical) -->
          @if (resizing()) {
            @if (resizePreview(); as rp) {
              <div class="event drag-ghost"
                [style.grid-row]="(rp.resourceIndex + 1)"
                [style.grid-column]="(rp.slotIndex + 1) + ' / span ' + rp.spanSlots"
                [style.background]="resizeEvent() ? eventColor(resizeEvent()!) : 'rgba(0,0,0,0.1)'">
                <div class="event-title">{{ resizeEvent()?.title || 'Event' }}</div>
                <div class="event-time">{{ resizeEvent()?.start | date:'shortTime' }}
                  - {{ resizeEvent()?.end | date:'shortTime' }}
                </div>
              </div>
            }
          }

          <!-- events overlay: switch row/column logic -->
          @for (res of resources(); let row = $index; track row) {
            @for (ev of (eventsByResource()[res.id] || []); track ev?.id ?? $index) {
              <div class="event" [attr.role]="ev.extendedProps?.['isHold'] ? 'presentation' : 'button'" [attr.tabindex]="ev.extendedProps?.['isHold'] ? -1 : 0"
                [attr.aria-label]="ev.extendedProps?.['isHold'] ? ev.extendedProps?.['ariaLabel'] : null"
                [ngClass]="{ compact: eventSpanSlots(ev) <= 1, dragging: dragging() && (dragEvent()?.id === ev.id), 'hovered-resource': hoveredResourceIndex() === row, 'hovered-slot': coversSlot(ev._start, ev._end, hoveredSlotIndex()), hold: ev.extendedProps?.['isHold'] }"
                [style.grid-row]="(row + 1)"
                [style.grid-column]="(slotIndexFor(ev._start) + 1) + ' / span ' + eventSpanSlots(ev)"
                [style.background]="ev.extendedProps?.['isHold'] ? null : eventColor(ev, res)"
                   [style.width]="'calc(100% / ' + ev._lanes + ')'
                   "
                [style.margin-left]="'calc((' + ev._lane + ') * (100% / ' + ev._lanes + '))'"
                [style.margin-bottom]="(ev._lane === ev._lanes - 1) ? 'var(--event-gutter)' : '16px'"
                [attr.draggable]="false"
                [style.cursor]="ev.extendedProps?.['isHold'] ? 'default' : (isDragEnabled() ? 'grab' : 'pointer')"
                (dragstart)="$event.preventDefault()"
                (contextmenu)="$event.preventDefault()"
                nz-popover
                [nzPopoverTrigger]="ev.extendedProps?.['isHold'] ? null : 'hover'"
                [nzPopoverTitle]="(ev.start | date: 'dd.MM.yyyy') + ' | ' + (ev.start | date:'shortTime') + ' - ' + (ev.end | date: 'shortTime')"
                [nzPopoverContent]="outerPopoverTplV"
                [nzPopoverMouseEnterDelay]="0.15"
                (click)="$event.stopPropagation(); onEventClickGuard(ev)"
                (keydown.enter)="onEventClickGuard(ev)"
                (pointerdown)="onEventPointerDown(ev, $event)">
                @if (isResizeEnabled() && !ev.extendedProps?.['isHold']) {
                  <!-- resize handles for vertical (left/right) -->
                  <div class="resize-handle handle-start vertical"
                  (pointerdown)="$event.stopPropagation(); onResizePointerDown(ev, 'start', $event)"></div>
                  <div class="resize-handle handle-end vertical"
                  (pointerdown)="$event.stopPropagation(); onResizePointerDown(ev, 'end', $event)"></div>
                }
                @if (eventTemplate) {
                  <ng-container [ngTemplateOutlet]="eventTemplate!.template"
                  [ngTemplateOutletContext]="{ $implicit: ev, event: ev, resource: res, viewMode: effectiveViewMode() }"></ng-container>
                } @else {
                  <div class="event-title">{{ ev.title || 'Event' }} {{ res.title }}</div>
                  <div class="event-time">{{ ev._start | date:'shortTime' }} - {{ ev._end | date:'shortTime' }}</div>
                }
                <ng-template #outerPopoverTplV>
                  <ng-container [ngTemplateOutlet]="eventPopoverTemplate?.template || eventPopoverTplV"
                  [ngTemplateOutletContext]="{ $implicit: ev, event: ev, resource: res, viewMode: effectiveViewMode() }"></ng-container>
                </ng-template>
                <ng-template #eventPopoverTplV>
                  <div class="popover-content">
                    <div class="pop-title">{{ ev.title || 'Event' }}</div>
                    <div class="pop-line">{{ ev._start | date:'shortTime' }} - {{ ev._end | date:'shortTime' }}</div>
                    <div class="pop-line">{{ res.title }}</div>
                  </div>
                </ng-template>
              </div>
            }
          }
        </div>
      </div>
    </div>
  }
</div>
